/**
 * Certificate Layout, Typography and Formatting Utilities
 */

export const CERTIFICATE_FONTS = [
  { label: 'System Default (Inter / Sans)', value: 'inherit', category: 'Standard' },
  { label: 'Cinzel (Imperial Roman / Academic)', value: "'Cinzel', serif", category: 'Formal Serif & Diploma' },
  { label: 'Cinzel Decorative (Ornate Heading)', value: "'Cinzel Decorative', serif", category: 'Formal Serif & Diploma' },
  { label: 'Playfair Display (Luxury Editorial)', value: "'Playfair Display', serif", category: 'Formal Serif & Diploma' },
  { label: 'Cormorant Garamond (Prestige Classic)', value: "'Cormorant Garamond', serif", category: 'Formal Serif & Diploma' },
  { label: 'Merriweather (Refined Book Serif)', value: "'Merriweather', serif", category: 'Formal Serif & Diploma' },
  { label: 'Times New Roman (Classic University)', value: "'Times New Roman', Times, serif", category: 'Formal Serif & Diploma' },
  { label: 'Georgia (Traditional Serif)', value: "Georgia, serif", category: 'Formal Serif & Diploma' },
  { label: 'Great Vibes (Flowing Diploma Script)', value: "'Great Vibes', cursive", category: 'Calligraphy & Script' },
  { label: 'Alex Brush (Regal Signature Script)', value: "'Alex Brush', cursive", category: 'Calligraphy & Script' },
  { label: 'Pinyon Script (Royal Calligraphy)', value: "'Pinyon Script', cursive", category: 'Calligraphy & Script' },
  { label: 'MonteCarlo (Formal Invitation Script)', value: "'MonteCarlo', cursive", category: 'Calligraphy & Script' },
  { label: 'Dancing Script (Casual Script)', value: "'Dancing Script', cursive", category: 'Calligraphy & Script' },
  { label: 'Montserrat (Modern Geometric Sans)', value: "'Montserrat', sans-serif", category: 'Modern Sans-Serif' },
  { label: 'Outfit (Clean Modern Sans)', value: "'Outfit', sans-serif", category: 'Modern Sans-Serif' },
  { label: 'Arial / Helvetica (Standard Sans)', value: "Arial, Helvetica, sans-serif", category: 'Modern Sans-Serif' },
  { label: 'Courier New (Monospace / Serial Code)', value: "'Courier New', Courier, monospace", category: 'Monospace' },
];

export const DEFAULT_CERTIFICATE_LAYOUT = {
  studentName: {
    top: 30,
    left: 10,
    fontSize: 26,
    fontWeight: 'bold',
    fontFamily: "'Playfair Display', serif",
    textAlign: 'left',
    color: '#1a1a1a',
    visible: true,
    maxWidth: 85,
  },
  courseName: {
    top: 42,
    left: 10,
    fontSize: 18,
    fontWeight: 'normal',
    fontFamily: "'Cinzel', serif",
    textAlign: 'left',
    color: '#444444',
    visible: true,
    maxWidth: 80,
  },
  certificateNumber: {
    top: 54,
    left: 10,
    fontSize: 12,
    fontWeight: 'normal',
    fontFamily: "'Courier New', Courier, monospace",
    textAlign: 'left',
    color: '#666666',
    visible: true,
    maxWidth: 60,
  },
  issueDate: {
    top: 64,
    left: 10,
    fontSize: 12,
    fontWeight: 'normal',
    fontFamily: 'inherit',
    textAlign: 'left',
    color: '#666666',
    visible: true,
    maxWidth: 60,
  },
  finalGrade: {
    top: 74,
    left: 10,
    fontSize: 16,
    fontWeight: 'normal',
    fontFamily: "'Cinzel', serif",
    textAlign: 'left',
    color: '#1a1a1a',
    prefix: 'Grade: ',
    visible: true,
    maxWidth: 60,
  },
};

export const normalizeCertificateLayout = (layout) => {
  if (!layout) return DEFAULT_CERTIFICATE_LAYOUT;
  return {
    ...DEFAULT_CERTIFICATE_LAYOUT,
    ...layout,
    studentName: { ...DEFAULT_CERTIFICATE_LAYOUT.studentName, ...(layout.studentName || {}) },
    courseName: { ...DEFAULT_CERTIFICATE_LAYOUT.courseName, ...(layout.courseName || {}) },
    certificateNumber: { ...DEFAULT_CERTIFICATE_LAYOUT.certificateNumber, ...(layout.certificateNumber || {}) },
    issueDate: { ...DEFAULT_CERTIFICATE_LAYOUT.issueDate, ...(layout.issueDate || {}) },
    finalGrade: { ...DEFAULT_CERTIFICATE_LAYOUT.finalGrade, ...(layout.finalGrade || {}) },
  };
};

/**
 * Formats certificate text by converting literal \n strings to real newlines.
 */
export const formatCertificateText = (text) => {
  if (!text) return '';
  return String(text).replace(/\\n/g, '\n');
};

/**
 * Calculates CSS transform based on text alignment.
 */
export const getCertificateFieldTransform = (textAlign) => {
  if (textAlign === 'center') return 'translate(-50%, -50%)';
  if (textAlign === 'right') return 'translate(-100%, -50%)';
  return 'translate(0%, -50%)';
};

/**
 * Normalizes grade strings to modern classifications matching the updated rubric:
 * 'Distinction' -> 'Distinction Pass'
 * 'Merit' -> 'Merit Pass'
 * 'Fail' -> 'Not Qualified'
 */
export const normalizeCertificateClassification = (grade) => {
  if (!grade) return 'Pass';
  const g = String(grade).trim();
  const lower = g.toLowerCase();
  if (lower === 'distinction') return 'Distinction Pass';
  if (lower === 'merit') return 'Merit Pass';
  if (lower === 'fail') return 'Not Qualified';
  if (lower === 'grade: distinction') return 'Grade: Distinction Pass';
  if (lower === 'grade: merit') return 'Grade: Merit Pass';
  if (lower === 'grade: fail') return 'Grade: Not Qualified';
  return g;
};

